import React, { useState, useEffect } from 'react';
import {
  X,
  Download,
  FileSpreadsheet,
  FileText,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react';
import api from '../../api';

interface ComprehensiveExportDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  defaultReportType?: string;
  defaultFormat?: string;
  context?: 'dashboard' | 'standalone';
}

interface ExportConfig {
  reportType: string;
  format: string;
  includeCharts: boolean;
  includeRawData: boolean;
  filename?: string;
}

const ComprehensiveExportDialog: React.FC<ComprehensiveExportDialogProps> = ({
  open,
  onClose,
  projectId,
  defaultReportType = 'comprehensive_rcsa',
  defaultFormat = 'excel',
}) => {
  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    reportType: defaultReportType,
    format: defaultFormat,
    includeCharts: true,
    includeRawData: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reportTypeLabels = {
    executive_dashboard: 'Executive Dashboard',
    process_owner_dashboard: 'Process Owner Dashboard',
    risk_expert_dashboard: 'Risk Expert Dashboard',
    control_assessment: 'Control Assessment',
    risk_assessment: 'Risk Assessment',
    residual_risk: 'Residual Risk Analysis',
    control_gaps: 'Control Gaps Report',
    remediation_actions: 'Remediation Actions',
    testing_history: 'Testing History',
    risk_appetite: 'Risk Appetite Report',
    comprehensive_rcsa: 'Comprehensive RCSA',
    audit_trail: 'Audit Trail',
    compliance_report: 'Compliance Report'
  };

  const formatOptions = [
    { value: 'excel', label: 'Excel', icon: FileSpreadsheet, color: 'text-green-600' },
    { value: 'pdf', label: 'PDF', icon: FileText, color: 'text-red-600' },
    { value: 'csv', label: 'CSV', icon: FileSpreadsheet, color: 'text-blue-600' }
  ];

  const reportTypes = [
    'comprehensive_rcsa',
    'executive_dashboard',
    'process_owner_dashboard',
    'risk_expert_dashboard',
    'control_assessment',
    'risk_assessment',
    'residual_risk',
    'control_gaps',
    'remediation_actions',
    'testing_history',
    'risk_appetite',
    'audit_trail',
    'compliance_report'
  ];

  const executeExport = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      console.log('Exporting with config:', exportConfig);

      // Call backend API using axios api client (with automatic token refresh)
      const response = await api.post(`/api/comprehensive-export/export/${projectId}`, {
        reportType: exportConfig.reportType,
        format: exportConfig.format,
        includeCharts: exportConfig.includeCharts,
        includeRawData: exportConfig.includeRawData,
        filename: exportConfig.filename
      }, {
        responseType: 'blob' // Important for file downloads
      });

      // Handle file download
      const blob = response.data;
      const contentDisposition = response.headers['content-disposition'];
      let filename = exportConfig.filename;

      // Extract filename from Content-Disposition header if not provided
      if (!filename && contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Fallback filename
      if (!filename) {
        filename = `export_${new Date().toISOString().split('T')[0]}.${exportConfig.format}`;
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Export error:', error);
      setError(error instanceof Error ? error.message : 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setExportConfig({
      reportType: defaultReportType,
      format: defaultFormat,
      includeCharts: true,
      includeRawData: false,
    });
    setError(null);
    setSuccess(false);
  };

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Comprehensive Export
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                Export completed successfully! Download should start automatically.
              </div>
            </div>
          )}

          {/* Form Content */}
          <div className="space-y-6">
            {/* Report Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Type
              </label>
              <select
                value={exportConfig.reportType}
                onChange={(e) => setExportConfig(prev => ({ ...prev, reportType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {reportTypes.map(type => (
                  <option key={type} value={type}>
                    {reportTypeLabels[type as keyof typeof reportTypeLabels] || type}
                  </option>
                ))}
              </select>
            </div>

            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Export Format
              </label>
              <div className="grid grid-cols-3 gap-3">
                {formatOptions.map(format => {
                  const Icon = format.icon;
                  return (
                    <button
                      key={format.value}
                      onClick={() => setExportConfig(prev => ({ ...prev, format: format.value }))}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        exportConfig.format === format.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-8 h-8 mx-auto mb-2 ${format.color}`} />
                      <div className="text-sm font-medium">{format.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Export Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Export Options
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportConfig.includeCharts}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, includeCharts: e.target.checked }))}
                    className="mr-2"
                  />
                  Include Charts and Visualizations
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportConfig.includeRawData}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, includeRawData: e.target.checked }))}
                    className="mr-2"
                  />
                  Include Raw Data
                </label>
              </div>
            </div>

            {/* Custom Filename */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Filename (optional)
              </label>
              <input
                type="text"
                value={exportConfig.filename || ''}
                onChange={(e) => setExportConfig(prev => ({ ...prev, filename: e.target.value }))}
                placeholder="Leave empty for auto-generated filename"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={executeExport}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveExportDialog;